import { useCallback, useMemo } from "react";
import { colors, font, spacing } from "@/config/theme";
import { formatMillisecondsToTime } from "@/time-tracking/utils/time";
import { PlusOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Button, Card } from "antd";
import dayjs from "dayjs";
import { nanoid } from "nanoid";
import { TIMESHEET_ENTRY_TYPES } from "@/time-tracking/types/Timesheet";
import { useTimesheetData } from "@/time-tracking/store/timesheet";
import EntryItem from "./EntryItem";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxs};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DateText = styled.p`
  font-weight: 700;
  font-size: ${font.size[16]};
  color: ${colors.gray[900]};
`;

const TimeNumber = styled.span`
  font-size: ${font.size[16]};
  font-weight: 600;
  color: ${colors.daybreakBlue[600]};
`;

const EntriesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

interface TimeTrackingCardProps {
  date: string;
  disableEditing?: boolean;
}

const TimeTrackingCard = ({
  date,
  disableEditing = false,
}: TimeTrackingCardProps) => {
  const { timesheetData, updateTimesheetCardEntriesByDate } =
    useTimesheetData();

  const { entries } = timesheetData[date] || { entries: [] };

  const handleDeleteEntry = useCallback(
    (entryId: number | string) => {
      const updatedEntries = entries.filter((entry) => entry.id !== entryId);
      updateTimesheetCardEntriesByDate(date, updatedEntries);
    },
    [entries, date, updateTimesheetCardEntriesByDate]
  );

  const handleUpdateEntry = useCallback(
    (entryId: number | string, updatedFields: Partial<(typeof entries)[0]>) => {
      const updatedEntries = entries.map((entry) =>
        entry.id === entryId ? { ...entry, ...updatedFields } : entry
      );
      updateTimesheetCardEntriesByDate(date, updatedEntries);
    },
    [entries, date, updateTimesheetCardEntriesByDate]
  );

  const renderEntryItems = useCallback(() => {
    if (entries.length === 0) {
      return null;
    }
    return entries.map((entry) => {
      return (
        <EntryItem
          key={`${entry.TimesheetWeekId}-${entry.id}`}
          entry={entry}
          handleDeleteEntry={handleDeleteEntry}
          handleUpdateEntry={handleUpdateEntry}
          disableEditing={disableEditing}
        />
      );
    });
  }, [entries, handleDeleteEntry, handleUpdateEntry, disableEditing]);

  const handleAddCategoryClick = () => {
    const newEntry = {
      id: `temp-id-${nanoid(5)}`,
      date,
      type:
        entries.length === 0
          ? "REGULAR"
          : (undefined as keyof typeof TIMESHEET_ENTRY_TYPES | undefined),
      time: "0",
      hours: 0,
      minutes: 0,
    };
    updateTimesheetCardEntriesByDate(date, [...entries, newEntry]);
  };

  const calculatedTotalMilliseconds = useMemo(() => {
    const totalMilliseconds = entries.reduce((acc, entry) => {
      return Number(entry.time) + acc;
    }, 0);
    return totalMilliseconds;
  }, [entries]);

  return (
    <Card>
      <Wrapper>
        <Header>
          <DateText>{dayjs.utc(date).format("dddd, MMM DD YYYY")}</DateText>
          <TimeNumber>
            {formatMillisecondsToTime(calculatedTotalMilliseconds)}
          </TimeNumber>
        </Header>
        <div>
          <EntriesList>{renderEntryItems()}</EntriesList>
        </div>
        {!disableEditing && (
          <div>
            <Button icon={<PlusOutlined />} onClick={handleAddCategoryClick}>
              Add Category
            </Button>
          </div>
        )}
      </Wrapper>
    </Card>
  );
};

export default TimeTrackingCard;
